// GEOBAJA Supabase Client

import { getAuthItem, setAuthItem, clearAuth } from './authStorage.js';
import { outbox } from './offlineQueue.js';
import { ENTORNO } from './entorno.js';
import {
  almacenEsDe, clasificarFalloAuth, clasificarRenovacion, leerJwt, motivoDeRechazo, subDelToken,
  textoAcceso, tipoFalloRed, urlRestablecer,
} from './acceso/acceso.logic.js';

const SUPABASE_URL = ENTORNO.SUPABASE_URL;
const SUPABASE_KEY = ENTORNO.SUPABASE_KEY;
const IS_DEV       = !ENTORNO.PRODUCCION;

// En produccion no se exponen detalles de DB en consola (CWE-209)
const devLog = IS_DEV
  ? (...args) => console.error(...args)
  : () => {};

// ══════════════════════════════════════════════════════════════════
//  SANITIZACIÓN DE ERRORES — CWE-209
// ══════════════════════════════════════════════════════════════════
// PostgREST devuelve mensajes de Postgres que pueden filtrar nombres de
// tablas, columnas y constraints (ej. "column perfiles.rol does not exist").
// Esta capa traduce códigos conocidos a mensajes amigables y preserva
// el código original para branching del caller. El error crudo solo se
// expone en dev via _raw.

// Mapeo Postgres SQLSTATE + códigos PostgREST → mensaje amigable
const FRIENDLY_BY_CODE = {
  // Postgres SQLSTATE
  '23505': 'Ya existe un registro con esos datos.',
  '23503': 'No se puede completar la operación: hay registros relacionados.',
  '23502': 'Falta información obligatoria.',
  '23514': 'Los datos no cumplen con las reglas de validación.',
  '22001': 'Uno de los campos excede el largo permitido.',
  '22003': 'Un valor numérico está fuera del rango permitido.',
  '22007': 'Formato de fecha inválido.',
  '22P02': 'Formato de dato inválido.',
  '40001': 'Conflicto de concurrencia. Intenta de nuevo.',
  '40P01': 'Conflicto temporal en la base de datos. Intenta de nuevo.',
  '42501': 'No tienes permisos para realizar esta acción.',
  '42P01': 'Recurso no disponible.',
  '42703': 'Recurso no disponible.',
  'P0001': 'La operación no es válida en este momento.',
  // PostgREST
  'PGRST116': 'No se encontró el registro solicitado.',
  'PGRST301': 'Sesión vencida. Inicia sesión nuevamente.',
  'PGRST204': 'Recurso no disponible.',
};

// Patrones de mensaje (fallback cuando no hay code reconocido) —
// se evalúan en orden y solo capturan errores explícitos del cliente,
// no detalles internos.
const FRIENDLY_BY_PATTERN = [
  [/jwt expired|invalid jwt|jwt invalid/i,         'Sesión vencida. Inicia sesión nuevamente.'],
  [/network|failed to fetch|networkerror/i,         'Error de conexión. Verifica tu internet e intenta de nuevo.'],
  [/permission denied|insufficient_privilege/i,     'No tienes permisos para realizar esta acción.'],
  [/email rate limit|over_email_send_rate_limit/i,  'Demasiados intentos. Espera unos minutos.'],
  [/invalid login credentials/i,                    'Correo o contraseña incorrectos.'],
  [/email not confirmed/i,                          'Confirma tu correo antes de iniciar sesión.'],
  [/weak.password|password.{0,20}weak/i,            'La contraseña no es suficientemente segura.'],
];

const FRIENDLY_DEFAULT = 'Error al procesar la solicitud. Intenta de nuevo o contacta soporte si el problema persiste.';

function sanitizeError(rawErr) {
  if (!rawErr) return null;
  // Si vino como string (algunos catch usan e.message directo), envolver
  const obj = typeof rawErr === 'string' ? { message: rawErr } : rawErr;
  const code = obj.code || obj.error_code || obj.error || '';
  let friendly = FRIENDLY_BY_CODE[code];
  if (!friendly && obj.message) {
    const matched = FRIENDLY_BY_PATTERN.find(([re]) => re.test(obj.message));
    if (matched) friendly = matched[1];
  }
  if (!friendly) friendly = FRIENDLY_DEFAULT;
  return {
    message: friendly,
    code,
    _raw: IS_DEV ? obj : undefined,  // solo en dev para inspección en consola
  };
}

// ══════════════════════════════════════════════════════════════════
//  CAPTURA OFFLINE — cola de escrituras de campo
// ══════════════════════════════════════════════════════════════════
// Solo tablas de CAPTURA QC en obra. Cuando una escritura a una de estas
// tablas falla por red (o ya no hay conexión), el intento se guarda en el
// outbox (offlineQueue.js) y se reejecuta al volver la señal, en el mismo
// orden FIFO en que se capturó (padres antes que hijos).
//
// Deliberadamente FUERA de la lista:
// - perfiles         → heartbeat cada 60s; encolarlo inundaría el outbox.
// - obras            → auto-activación dentro de loadData (write-on-read) e
//                      IDs secuenciales de texto (newId) que colisionan entre
//                      dispositivos offline.
// - entregas_cliente / comp_entregas / comp_publicaciones → despacho y
//   publicación son operaciones de OFICINA que además generan ZIP/PDF con
//   librerías de CDN: requieren red de todos modos.
// - mensajes/*       → ya tienen su propia mecánica optimista + realtime.
const OFFLINE_TABLES = new Set([
  // Concreto (captura en obra)
  'colados', 'camiones_colado', 'muestras', 'especimenes', 'verificaciones',
  // Compactación (captura en obra + catálogos de apoyo dados de alta en campo)
  'comp_reportes', 'comp_ensayes', 'comp_humedades', 'comp_analisis_proctor',
  'comp_conos', 'comp_ubicaciones', 'comp_frentes', 'comp_estructuras',
  'comp_capas', 'comp_imagenes_frente', 'comp_reporte_auditoria', 'comp_borradores',
  // La app de campo (2026-10-27): la visita, los hechos de cada cala y la foto de evidencia se capturan sin señal.
  'comp_visitas', 'comp_cala_eventos', 'comp_fotos',
  // Laboratorio (registro de campo: sondeos y muestras SIN folio — el folio
  // nace en lab_recibir_muestra, que es online-only a propósito)
  'lab_sondeos', 'lab_muestras', 'lab_muestra_eventos',
]);

// RPCs de captura encolables. crear_muestra_con_especimenes acepta un id de
// muestra generado en cliente (migración crear_muestra_acepta_id_cliente):
// el replay repetido choca en PK (23505) y se reconoce como "ya aplicado".
// comp_asignar_numero_prueba NO es encolable: es un consecutivo del servidor
// cuyo resultado consume la UI — offline queda sin asignar y se resuelve en
// la siguiente captura con red.
// lab_registrar_muestra sigue el mismo contrato: acepta id de cliente en
// p_muestra y p_ensayes, así el replay choca en PK y 23505 = ya aplicado.
// lab_recibir_muestra (folio) y lab_cambiar_estado (custodia) NO se encolan:
// la primera es un consecutivo del server; la segunda replicada duplicaría
// eventos de custodia en una tabla donde nada se puede borrar.
const OFFLINE_RPCS = new Set(['crear_muestra_con_especimenes', 'lab_registrar_muestra']);

// ══════════════════════════════════════════════════════════════════
//  ESCRITURA SIN EFECTO — el [] que no es error
// ══════════════════════════════════════════════════════════════════
// Un UPDATE o DELETE que no tocó ninguna fila responde 200 con [] y SIN
// error: PostgREST no distingue "RLS te filtró la fila" de "el filtro no
// encontró nada". En un update de UNA fila por id que el llamador sabe que
// existe, ese [] es una negación de permisos disfrazada de éxito, y
// confirmarla en el estado local es justo la mentira que esta app no puede
// permitirse: el folio avanza, la factura se da por eliminada.
//
// Ésta es la ÚNICA definición de esa pregunta. Se resolvía a mano en 26
// lugares y NINGUNO descontaba la respuesta sintética del modo offline: el
// DELETE encolado también devuelve [] (marcado con __offline) y ahí no hay
// negación, hay falta de señal.
//
// Ojo: no todo [] es fallo. Un delete idempotente (las facturas de un cobro
// que no tiene), un update por filtro (las que sigan en 'entregado') o el
// guard de concurrencia por `revision` devuelven [] legítimamente. Por eso
// el predicado solo INFORMA; cada llamador decide si en su escritura eso es
// un fallo.
export const sinFilas = (res) => Array.isArray(res) && res.length === 0 && !res.__offline;

// Tablas cuyo INSERT no pide la fila de vuelta.
// PostgREST manda `Prefer: return=representation` por default, y eso es un
// INSERT ... RETURNING: Postgres exige politica de SELECT sobre la fila recien
// escrita y, si no la hay, revienta el INSERT COMPLETO con 42501 (probado).
// La bitacora de compactacion la LEE solo el administrador (RLS) mientras que
// la ESCRIBE cualquier personal interno — pedir el eco mataria el registro de
// todos los demas, que es justamente lo que no debe faltar nunca.
const TABLAS_SIN_ECO = new Set(['comp_reporte_auditoria']);

// Timeouts: en campo con 1 barra de señal, un fetch sin timeout puede colgar
// minutos. Cortamos y encolamos: el replay es idempotente (UUID de cliente),
// así que abortar un request que SÍ llegó no duplica datos.
// Payloads grandes (croquis/imágenes base64 de MBs) necesitan más aire: con
// un timeout fijo corto jamás terminarían en red lenta y se encolarían para
// siempre (el replay cortaría igual). Escala por tamaño del cuerpo.
const OFFLINE_ATTEMPT_MS = 15000;   // base intento en vivo
const OFFLINE_REPLAY_MS  = 30000;   // base replay de fondo
const OFFLINE_MAX_MS     = 120000;  // techo (croquis grande en 3G)
const attemptMs = (payload, base) => {
  try {
    // +1s por cada 10 KB de cuerpo: proporcional, con techo de 2 min.
    const bytes = JSON.stringify(payload).length;
    return Math.min(OFFLINE_MAX_MS, base + Math.ceil(bytes / 10240) * 1000);
  } catch (e) { return base; }
};

// UUID v4 generado en cliente: las tablas QC tienen DEFAULT gen_random_uuid(),
// que solo aplica cuando id viene AUSENTE — mandar id explícito es compatible.
const genId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0'));
  return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h.slice(6, 8).join('')}-${h.slice(8, 10).join('')}-${h.slice(10).join('')}`;
};

// Falla de RED (vs rechazo del servidor): fetch lanza TypeError sin respuesta;
// AbortSignal.timeout lanza TimeoutError/AbortError. Un 4xx/5xx NO entra aquí.
const isNetError = (e) =>
  e instanceof TypeError || (e && (e.name === 'AbortError' || e.name === 'TimeoutError'));

const timeoutSignal = (ms) =>
  (typeof AbortSignal !== 'undefined' && AbortSignal.timeout) ? AbortSignal.timeout(ms) : undefined;

const isOnline = () => typeof navigator === 'undefined' || navigator.onLine !== false;

// ══════════════════════════════════════════════════════════════════
//  SESIÓN: tiempos límite y renovación
// ══════════════════════════════════════════════════════════════════
// Toda llamada a /auth/v1 lleva tiempo límite (AbortController + setTimeout, no
// AbortSignal.timeout: el bundle apunta a es2018 y hay equipos viejos). Sin él,
// entrar con una barra de señal dejaba «Verificando…» minutos y cerrar sesión
// dejaba la pantalla con los datos de la persona mientras esperaba.
const AUTH_TIEMPO_MS    = 15000;   // entrar, recuperar, verificar, guardar, renovar
const AUTH_SALIDA_MS    = 4000;    // cerrar sesión: nunca se espera más
// La renovación se serializa entre ventanas (PWA con dos ventanas, pestaña
// duplicada): Auth anula la sesión entera si recibe un refresh token de dos
// rotaciones atrás. La espera del candado cubre una renovación ajena completa.
const CANDADO_RENOVAR   = 'geobaja-auth-refresh';
const CANDADO_ESPERA_MS = AUTH_TIEMPO_MS + 5000;
// Sin red la sesión se conserva y se reintenta con espera creciente.
const REINTENTO_MIN_MS  = 5000;
const REINTENTO_MAX_MS  = 5 * 60 * 1000;
// Un token guardado por otra ventana se adopta sin renovar si le queda al menos esto.
const MARGEN_VIGENCIA_MS = 60 * 1000;

// Cliente Supabase — nuevo formato sb_publishable
// Regla clave: publishable key SOLO en header "apikey", NUNCA en Authorization
// Authorization lleva el JWT del usuario autenticado (o se omite para anon)
const sb = (() => {
  const apiHeaders = (userJwt) => {
    const h = {
      "apikey":       SUPABASE_KEY,
      "Content-Type": "application/json",
      "Prefer":       "return=representation",
    };
    if (userJwt) h["Authorization"] = `Bearer ${userJwt}`;
    return h;
  };

  let _token            = null;
  let _refreshToken     = null;
  let _user             = null;
  let _refreshPromise   = null;  // renovación en curso en esta ventana (una sola a la vez)
  let _onSessionExpired = null;  // aviso de sesión cerrada: recibe el motivo
  let _duenoCola        = null;  // perfil ACEPTADO cuya cola offline puede subir
  let _ultimoDueno      = null;  // el último dueño aceptado, también ya cerrada su sesión
  let _colaEnEspera     = false; // sesión restaurada: la cola cuenta lo suyo pero no sube hasta que respondan Auth y el perfil
  let _reintentoTimer   = null;
  let _reintentoEspera  = 0;

  // ── Una llamada a /auth/v1 ────────────────────────────────────────
  // Resultado uniforme (ResultadoAuth, documentado en acceso/acceso.logic.js):
  //   { ok, status, datos, cuerpo, errorRed }
  // Nunca lanza. status 0 + errorRed cuando no hubo respuesta; un 2xx con un
  // cuerpo que no es JSON (portal cautivo, proxy) cuenta como fallo.
  // keepalive: la petición sigue aunque la página se cierre (cerrar la sesión de un enlace al salir).
  const llamarAuth = async (ruta, { metodo = "POST", token = null, cuerpo, tiempoMs = AUTH_TIEMPO_MS, keepalive = false } = {}) => {
    if (!isOnline()) return { ok: false, status: 0, datos: null, cuerpo: null, errorRed: "sin_red" };
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    let reloj = null;
    const vencida = new Promise((_, rechazar) => {
      reloj = setTimeout(() => {
        try { if (ctrl) ctrl.abort(); } catch (e) { /* sin abort */ }
        const err = new Error("Tiempo de espera agotado");
        err.name = "TimeoutError";
        rechazar(err);
      }, tiempoMs);
    });
    const headers = { "apikey": SUPABASE_KEY };
    if (cuerpo !== undefined) headers["Content-Type"] = "application/json";
    if (token) headers["Authorization"] = `Bearer ${token}`;
    try {
      const peticion = (async () => {
        const r = await fetch(`${SUPABASE_URL}/auth/v1/${ruta}`, {
          method: metodo,
          headers,
          body: cuerpo !== undefined ? JSON.stringify(cuerpo) : undefined,
          signal: ctrl ? ctrl.signal : undefined,
          ...(keepalive ? { keepalive: true } : {}),
        });
        const texto = await r.text();
        return { r, texto };
      })();
      // La carrera garantiza el tiempo límite aunque el fetch ignore la señal.
      const { r, texto } = await Promise.race([peticion, vencida]);
      let json = null;
      if (texto) { try { json = JSON.parse(texto); } catch (e) { json = null; } }
      if (r.ok && (json !== null || !texto)) {
        return { ok: true, status: r.status, datos: json, cuerpo: null, errorRed: null };
      }
      devLog("auth", ruta.split("?")[0], r.status, json || texto);
      return { ok: false, status: r.status, datos: null, cuerpo: json !== null ? json : (texto || null), errorRed: null };
    } catch (e) {
      return { ok: false, status: 0, datos: null, cuerpo: null, errorRed: tipoFalloRed(e) || "sin_red" };
    } finally {
      clearTimeout(reloj);
    }
  };

  // Compatibilidad con los llamadores de antes (LoginScreen, DatosObraModal): el
  // fallo lleva error {message, code} con el texto del motor para esa operación
  // y, donde se pide, el éxito repite los campos de datos al nivel superior.
  const conCompat = (res, modo, { repetirDatos = false } = {}) => {
    if (!res.ok) {
      const f = clasificarFalloAuth(res);
      return { ...res, error: { message: textoAcceso(f.clase, { modo, esperaSeg: f.esperaSeg, razones: f.razones }).texto, code: f.codigo || "" } };
    }
    return repetirDatos && res.datos && typeof res.datos === "object" ? { ...res.datos, ...res } : res;
  };

  // ── Candado entre ventanas ────────────────────────────────────────
  // navigator.locks cuando existe (Chrome 69+, Safari 15.4+). Si no llega a tiempo
  // o la API falla antes de entrar, se sigue sin candado: un candado atorado no
  // puede dejar a la persona sin renovar.
  const conCandado = async (nombre, fn) => {
    const locks = typeof navigator !== "undefined" ? navigator.locks : null;
    if (!locks || typeof locks.request !== "function") return fn();
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const espera = ctrl ? setTimeout(() => ctrl.abort(), CANDADO_ESPERA_MS) : null;
    let dentro = false;
    try {
      return await locks.request(nombre, ctrl ? { signal: ctrl.signal } : {}, () => {
        dentro = true;
        if (espera) clearTimeout(espera);
        return fn();
      });
    } catch (e) {
      if (!dentro) return fn();
      throw e;
    } finally {
      if (espera) clearTimeout(espera);
    }
  };

  const expiraEl = (jwt) => {
    const c = leerJwt(jwt);
    return c && typeof c.exp === "number" ? c.exp * 1000 : 0;
  };

  const avisarCierre = (motivo) => {
    if (!_onSessionExpired) return;
    try { _onSessionExpired(motivo); } catch (e) { devLog("onSessionExpired", e); }
  };

  const fijarDueno = (id) => {
    _duenoCola = id || null;
    if (_duenoCola) _ultimoDueno = _duenoCola;
    try { outbox.setDueno(_duenoCola); } catch (e) { devLog("outbox.setDueno", e); }
  };

  // ¿El almacén (authStorage) guarda a esta persona? Solo entonces la renovación escribe ahí.
  const almacenGuardaA = (id) => almacenEsDe({
    tokenGuardado: getAuthItem("sb_token"),
    perfilGuardado: getAuthItem("sb_perfil"),
    usuarioId: id,
  });

  const cancelarReintento = () => {
    if (_reintentoTimer) clearTimeout(_reintentoTimer);
    _reintentoTimer = null;
  };

  const programarReintento = () => {
    if (_reintentoTimer || !_refreshToken) return;
    _reintentoEspera = _reintentoEspera ? Math.min(_reintentoEspera * 2, REINTENTO_MAX_MS) : REINTENTO_MIN_MS;
    _reintentoTimer = setTimeout(() => { _reintentoTimer = null; doRefresh(); }, _reintentoEspera);
  };

  // ── Renovación (dentro del candado) ───────────────────────────────
  // 'ok' | 'rechazado' | 'sin_red' | 'sin_sesion' (este último solo interno)
  const renovarDentro = async () => {
    // Otra ventana pudo rotar la sesión mientras esperábamos el candado: si el
    // almacén trae un token MÁS NUEVO de la MISMA persona, se adopta; si aún está
    // vigente, ni siquiera se renueva (renovar con el viejo arriesga la sesión).
    const refreshGuardado = getAuthItem("sb_refresh_token");
    const accesoGuardado = getAuthItem("sb_token");
    if (refreshGuardado && refreshGuardado !== _refreshToken && accesoGuardado && _token
        && subDelToken(accesoGuardado) && subDelToken(accesoGuardado) === subDelToken(_token)
        && expiraEl(accesoGuardado) > expiraEl(_token)) {
      _token = accesoGuardado;
      _refreshToken = refreshGuardado;
      if (expiraEl(accesoGuardado) - Date.now() > MARGEN_VIGENCIA_MS) return "ok";
    }
    if (!_refreshToken) {
      if (!_token) return "sin_sesion";
      // Hay token pero nada con qué renovarlo: sirve mientras no venza; vencido,
      // la sesión terminó.
      if (expiraEl(_token) > Date.now()) return "ok";
      const vencido = _token;
      const guardadoTok = getAuthItem("sb_token");
      _token = null;
      fijarDueno(null);
      if (!guardadoTok || guardadoTok === vencido) clearAuth();
      avisarCierre("sesion_cerrada");
      return "rechazado";
    }
    const usado = _refreshToken;
    const quien = subDelToken(_token);
    const res = await llamarAuth("token?grant_type=refresh_token", { cuerpo: { refresh_token: usado } });
    // La sesión cambió mientras volaba la petición (cerraron sesión, entró otra
    // persona): no se resucita nada, y «ok» solo si la vigente sigue siendo de la
    // misma persona. Con la de otra, quien esperaba esta renovación no reenvía nada.
    if (_refreshToken !== usado) return _token && subDelToken(_token) === quien ? "ok" : "sin_sesion";
    const estado = clasificarRenovacion(res);
    if (estado === "ok") {
      _token = res.datos.access_token;
      _refreshToken = res.datos.refresh_token || usado;
      // Persistir el token rotado en el MISMO almacén que lee App.js
      // (localStorage en app instalada, sessionStorage en pestaña), y solo si ese
      // almacén guarda a esta persona: otra ventana de la app instalada pudo guardar
      // la sesión de alguien más (sus tokens con el perfil de aquella abrían la app
      // con la identidad equivocada), y un almacén vacío es una salida que no se deshace.
      if (almacenGuardaA(subDelToken(_token))) {
        setAuthItem("sb_token", _token);
        setAuthItem("sb_refresh_token", _refreshToken);
      }
      return "ok";
    }
    if (estado === "rechazado") {
      // Rechazo autoritativo de Auth: la sesión ya no existe. Se limpia y se avisa
      // con el motivo, venga la llamada de donde venga (consulta, renovación
      // periódica o arranque). El almacén solo se borra si guardaba ESTA sesión.
      const guardado = getAuthItem("sb_refresh_token");
      _token = null;
      _refreshToken = null;
      fijarDueno(null);
      if (!guardado || guardado === usado) clearAuth();
      avisarCierre(motivoDeRechazo(res));
      return "rechazado";
    }
    devLog("Renovación sin respuesta útil:", res.status, res.errorRed);
    return "sin_red";
  };

  // ── Renueva el access_token ───────────────────────────────────────
  // Devuelve 'ok' | 'rechazado' | 'sin_red'. Solo 'rechazado' cierra la sesión (y
  // avisa una vez con el motivo). 'sin_red' conserva todo y programa un reintento.
  const doRefresh = () => {
    if (_refreshPromise) return _refreshPromise;
    const promesa = (async () => {
      let estado;
      try { estado = await conCandado(CANDADO_RENOVAR, renovarDentro); }
      catch (e) { devLog("Refresh failed:", e); estado = "sin_red"; }
      if (estado === "sin_red") programarReintento();
      else { _reintentoEspera = 0; cancelarReintento(); }
      return estado === "sin_sesion" ? "rechazado" : estado;
    })();
    _refreshPromise = promesa;
    promesa.then(() => { if (_refreshPromise === promesa) _refreshPromise = null; });
    return promesa;
  };

  // Al volver la red no se espera el siguiente reintento programado.
  if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
    window.addEventListener("online", () => {
      if (_reintentoTimer) { cancelarReintento(); doRefresh(); }
    });
  }

  // Envuelve el catch de las operaciones: mismo shape {error} de siempre,
  // más __network para que la capa offline distinga "red caída" de "rechazo".
  const errRes = (e) => {
    const res = { error: sanitizeError(e && e.message ? e.message : e) };
    if (isNetError(e)) res.__network = true;
    return res;
  };
  const netErrorRes = () => ({ error: sanitizeError('network request failed'), __network: true });

  // ¿La respuesta dice que el JWT ya no sirve? Solo se mira un FALLO: una
  // respuesta exitosa cuyo contenido dice «jwt expired» (un mensaje del chat, por
  // ejemplo) disparaba una renovación y reenviaba la escritura.
  const esJwtRechazado = async (r) => {
    if (r.status === 401) return true;
    if (r.ok) return false;
    let body = null;
    try { body = await r.clone().json(); } catch (e) { return false; }
    if (!body || typeof body !== "object") return false;
    const code = String(body.code || "");
    if (code === "PGRST301" || code === "PGRST303") return true;
    const msg = String(body.message || body.hint || body.error || "");
    return /jwt expired|jwt invalid|invalid jwt/i.test(msg);
  };

  const tokenDeCabeceras = (headers) => {
    const a = headers && (headers["Authorization"] || headers["authorization"]);
    return typeof a === "string" && a.indexOf("Bearer ") === 0 ? a.slice(7) : null;
  };

  // ── Ejecuta un fetch y reintenta 1 vez si el JWT expiró ──────────
  // - Solo se reenvía con un token de la MISMA persona que mandó la petición: si
  //   mientras volaba salió y entró otra, el 401 es la respuesta (una captura de A
  //   no se reenvía con la identidad de B). Sin token enviado tampoco se reintenta.
  // - Si otra llamada ya renovó mientras esta volaba, solo se reintenta.
  // - Renovación 'ok': reintenta con el token nuevo.
  // - 'sin_red': lanza un error de red; los llamadores lo convierten en
  //   { error, __network: true } y la capa offline encola la escritura.
  // - 'rechazado': la renovación ya avisó con su motivo; se devuelve el 401.
  const fetchWithRetry = async (url, opts) => {
    const enviado = tokenDeCabeceras(opts.headers);
    const r = await fetch(url, opts);
    if (!(await esJwtRechazado(r))) return r;
    if (!enviado) return r; // sin sesión al pedir: el 401 es la respuesta
    const quien = subDelToken(enviado);
    const deLaMismaPersona = () => !!_token && subDelToken(_token) === quien;
    if (!deLaMismaPersona()) return r;
    const estado = _token !== enviado ? "ok" : await doRefresh();
    if (estado === "ok" && deLaMismaPersona()) {
      opts.headers = { ...opts.headers, "Authorization": `Bearer ${_token}` };
      return fetch(url, opts);
    }
    if (estado === "sin_red") throw new TypeError("network request failed (renovación de sesión)");
    return r;
  };

  // ── Database cruda (sin cola) ─────────────────────────────────
  // _opts.signal permite timeout en las escrituras de campo. El shape de
  // retorno es idéntico al histórico; solo se añade __network en fallas de red.
  // PostgREST corta toda respuesta en db-max-rows (1000, default de Supabase).
  // Una lectura que llega EXACTA al corte se asume trunca y se re-lee completa
  // por páginas (limit/offset) con orden determinista: al order del caller se
  // le añade id como desempate — sin él, los empates pueden duplicar o saltar
  // filas entre páginas. Las tablas chicas nunca entran aquí. Si el proyecto
  // cambia db-max-rows, sincronizar esta constante.
  const PG_MAX_ROWS = 1000;
  const PG_MAX_PAGES = 50; // tope de seguridad: 50k filas
  // Las tablas que ya rebasaron el corte en este navegador (2026-10-23) se leen
  // paginadas desde la primera petición. Antes la primera respuesta (mil filas
  // recortadas, sin orden determinista) se descartaba y se volvía a bajar entera:
  // conceptos pagaba una página de más en cada arranque. Se recuerda en
  // localStorage (sin él, vale para la sesión); si el paginado no puede, la lectura
  // cae a la de siempre, así que recordar de más nunca pierde filas.
  const LLAVE_TABLAS_PAGINADAS = "gb_tablas_paginadas";
  const leerTablasPaginadas = () => {
    try {
      const v = JSON.parse(localStorage.getItem(LLAVE_TABLAS_PAGINADAS) || "[]");
      return new Set(Array.isArray(v) ? v.filter((t) => typeof t === "string") : []);
    } catch (e) { return new Set(); }
  };
  const _tablasPaginadas = leerTablasPaginadas();
  const recordarTablaPaginada = (table) => {
    if (_tablasPaginadas.has(table)) return;
    _tablasPaginadas.add(table);
    try { localStorage.setItem(LLAVE_TABLAS_PAGINADAS, JSON.stringify([..._tablasPaginadas])); } catch (e) { /* sin almacén */ }
  };
  const fetchTodasLasPaginas = async (table, cols, filters, opts) => {
    const conDesempate = /(^|&)order=/.test(filters)
      ? filters.replace(/(^|&)order=([^&]*)/, (m, pre, o) => `${pre}order=${o},id.asc`)
      : (filters ? filters + "&order=id.asc" : "order=id.asc");
    const todas = [];
    // Dedupe por id: entre página y página alguien puede insertar una fila, y
    // el offset se recorre repitiendo el renglón del borde. Gana la primera
    // aparición (el orden de lectura es el orden del documento).
    const vistos = new Set();
    for (let pagina = 0; pagina < PG_MAX_PAGES; pagina++) {
      const url = `${SUPABASE_URL}/rest/v1/${table}?select=${cols}&${conDesempate}` +
        `&limit=${PG_MAX_ROWS}&offset=${pagina * PG_MAX_ROWS}`;
      const r = await fetchWithRetry(url, opts);
      // Un cuerpo que no es JSON (un 502 con HTML del proxy, un corte a media
      // respuesta) lanzaba SyntaxError y convertía una lectura VÁLIDA en error
      // de conexión. Aquí solo se aborta el paginado: el caller conserva la
      // primera página, igual que cuando la página falla con error del server.
      let d = null;
      try {
        d = await r.json();
      } catch (e) {
        devLog("SELECT paginado: cuerpo ilegible", table, e);
        console.warn(`Lectura paginada de ${table}: respuesta ilegible en la página ${pagina + 1}; se conserva la primera página.`);
        return null;
      }
      // Falla una página (p.ej. la tabla no tiene id): el caller conserva la
      // primera respuesta, el comportamiento previo al paginado.
      if (!r.ok || !Array.isArray(d)) { devLog("SELECT paginado error", d); return null; }
      for (const fila of d) {
        if (fila && fila.id != null) {
          if (vistos.has(fila.id)) continue;
          vistos.add(fila.id);
        }
        todas.push(fila);
      }
      if (d.length < PG_MAX_ROWS) return todas;
    }
    devLog("SELECT paginado: tope de páginas alcanzado", table);
    return todas;
  };

  const rawFrom = (table) => ({
    select: async (cols = "*", filters = "", _opts) => {
      try {
        const opts = { headers: apiHeaders(_token), signal: _opts && _opts.signal };
        const sinLimite = !/(^|&)limit=/.test(filters);
        if (sinLimite && _tablasPaginadas.has(table)) {
          const completas = await fetchTodasLasPaginas(table, cols, filters, opts);
          if (completas) return completas;
        }
        const url = `${SUPABASE_URL}/rest/v1/${table}?select=${cols}${filters ? "&" + filters : ""}`;
        const r = await fetchWithRetry(url, opts);
        const d = await r.json();
        // La tabla y el filtro van en el aviso: "SELECT error Object" a secas no decia que lectura fallo (2026-10-23).
        if (!r.ok) { devLog("SELECT error", table, filters, d); return { error: sanitizeError(d) }; }
        if (!Array.isArray(d)) return [];
        if (d.length >= PG_MAX_ROWS && sinLimite) {
          const completas = await fetchTodasLasPaginas(table, cols, filters, opts);
          if (completas) { recordarTablaPaginada(table); return completas; }
        }
        return d;
      } catch(e) { return errRes(e); }
    },
    insert: async (data, _opts) => {
      try {
        const sinEco  = TABLAS_SIN_ECO.has(table);
        const headers = sinEco ? { ...apiHeaders(_token), "Prefer": "return=minimal" } : apiHeaders(_token);
        const opts = { method: "POST", headers, body: JSON.stringify(Array.isArray(data) ? data : [data]), signal: _opts && _opts.signal };
        const r = await fetchWithRetry(`${SUPABASE_URL}/rest/v1/${table}`, opts);
        // Sin eco: 201 con cuerpo VACIO — no hay JSON que leer (r.json() lanzaria
        // y convertiria un registro exitoso en error). El caller solo mira .error,
        // asi que se devuelve el payload como si el server lo hubiera repetido.
        if (sinEco) {
          if (!r.ok) {
            const err = await r.json().catch(() => ({}));
            devLog("INSERT error", err);
            return { error: sanitizeError(err) };
          }
          return Array.isArray(data) ? data : [data];
        }
        const d = await r.json();
        if (!r.ok) { devLog("INSERT error", d); return { error: sanitizeError(d) }; }
        return Array.isArray(d) ? d : [d];
      } catch(e) { return errRes(e); }
    },
    upsert: async (data, conflictCol = 'id', _opts) => {
      try {
        const headers = { ...apiHeaders(_token), "Prefer": "resolution=merge-duplicates,return=representation" };
        const opts = { method: "POST", headers, body: JSON.stringify(Array.isArray(data) ? data : [data]), signal: _opts && _opts.signal };
        const r = await fetchWithRetry(`${SUPABASE_URL}/rest/v1/${table}?on_conflict=${conflictCol}`, opts);
        const d = await r.json();
        if (!r.ok) { devLog("UPSERT error", d); return { error: sanitizeError(d) }; }
        return Array.isArray(d) ? d : [d];
      } catch(e) { return errRes(e); }
    },
    update: async (data, filter, _opts) => {
      try {
        const opts = { method: "PATCH", headers: apiHeaders(_token), body: JSON.stringify(data), signal: _opts && _opts.signal };
        const r = await fetchWithRetry(`${SUPABASE_URL}/rest/v1/${table}?${filter}`, opts);
        if (r.status === 204) return [];
        const d = await r.json();
        if (!r.ok) { devLog("UPDATE error", d); return { error: sanitizeError(d) }; }
        return Array.isArray(d) ? d : d;
      } catch(e) { return errRes(e); }
    },
    delete: async (filter, _opts) => {
      try {
        const opts = { method: "DELETE", headers: apiHeaders(_token), signal: _opts && _opts.signal };
        const r = await fetchWithRetry(`${SUPABASE_URL}/rest/v1/${table}?${filter}`, opts);
        if (r.status === 204) return [];
        const d = await r.json();
        if (!r.ok) { devLog("DELETE error", d); return { error: sanitizeError(d) }; }
        return Array.isArray(d) ? d : [];
      } catch(e) { return errRes(e); }
    },
  });

  const rawRpc = async (fn, params = {}, _opts) => {
    try {
      const r = await fetchWithRetry(`${SUPABASE_URL}/rest/v1/rpc/${fn}`, {
        method: "POST", headers: apiHeaders(_token), body: JSON.stringify(params), signal: _opts && _opts.signal,
      });
      const d = await r.json();
      if (!r.ok) { devLog("RPC error", fn, d); return { error: sanitizeError(d) }; }
      return d;
    } catch(e) { return errRes(e); }
  };

  // ── Capa offline sobre las tablas de captura ──────────────────
  // Respuesta sintética: mismo shape que PostgREST con return=representation
  // (array de filas con id) para que los callers que leen r[0].id sigan
  // funcionando. __offline=true la distingue para la UX futura. Un update
  // devuelve [payload] y NO []: varios flujos interpretan la respuesta
  // vacía como "sin permisos" (heurística RLS) y mostrarían un error falso.
  const syntheticRows = (rows) => {
    const arr = rows.map((r) => ({ ...r }));
    arr.__offline = true;
    return arr;
  };

  // Encola y devuelve la respuesta sintética; si IndexedDB falla (cuota,
  // navegador raro), devuelve el error de red real — nunca fingir éxito
  // sin haber persistido el intento.
  // perfilId: la cola solo sube este registro con la sesión de quien lo capturó. Es
  // quien tenía la sesión cuando EMPEZÓ la escritura (capturaDe), no cuando se encola:
  // una escritura viva que falla por red después de que otra persona entró sigue
  // siendo de quien la hizo. Sin nadie a quien atribuirla no se encola: un registro
  // nuevo sin dueño lo subiría y lo descartaría quien entre después.
  const queueAndFake = async (item, fallbackRes, synthetic, perfilId) => {
    if (!perfilId) { devLog('outbox: escritura sin sesión, no se encola', item.table); return fallbackRes; }
    try { await outbox.enqueue({ ...item, perfilId }); } catch (e) { devLog('outbox enqueue failed', e); return fallbackRes; }
    if (isOnline()) outbox.drain(); // habia red: drenar ya, no esperar el retry de 30s
    return synthetic;
  };

  // Mientras el outbox tenga items pendientes, TODA escritura de captura
  // entra a la cola aunque haya red: si una escritura viva se adelantara a
  // un padre aun encolado (timeout, senal intermitente), el hijo fallaria
  // por FK en el servidor, o un replay posterior pisaria una edicion mas
  // nueva. El FIFO preserva la causalidad captura -> sync.
  // Las capturas de OTRA persona (ajenos) no son padres de lo que se captura
  // ahora y no suben con esta sesión: no retienen la escritura viva.
  const debeEncolar = () => {
    if (!isOnline()) return true;
    const st = outbox.getStatus();
    return (st.pending - (st.ajenos || 0)) > 0;
  };

  // Quién captura: el perfil de la sesión (o el dueño del token si aún no hay perfil).
  const idDeSesion = () => (_user && _user.id) || subDelToken(_token) || null;
  // A quién pertenece una escritura de captura que empieza ahora. Sin sesión (un
  // respaldo con retraso que dispara tras cerrar sesión) es del último dueño aceptado
  // en esta ventana: sube cuando esa persona vuelva a entrar.
  const capturaDe = () => idDeSesion() || _ultimoDueno;

  const offlineFrom = (table) => {
    const raw = rawFrom(table);
    return {
      select: raw.select,
      insert: async (data) => {
        const quien = capturaDe();
        const rows = (Array.isArray(data) ? data : [data]).map((r) => (r && r.id) ? r : { ...r, id: genId() });
        const item = { table, op: 'insert', payload: rows };
        if (debeEncolar()) return queueAndFake(item, netErrorRes(), syntheticRows(rows), quien);
        const res = await raw.insert(rows, { signal: timeoutSignal(attemptMs(rows, OFFLINE_ATTEMPT_MS)) });
        if (res && res.error && res.__network) return queueAndFake(item, res, syntheticRows(rows), quien);
        return res;
      },
      upsert: async (data, conflictCol = 'id') => {
        // Sin id de cliente: el upsert resuelve por conflictCol y el replay
        // repetido es un merge idempotente por sí mismo.
        const quien = capturaDe();
        const rows = Array.isArray(data) ? data : [data];
        const item = { table, op: 'upsert', payload: rows, conflictCol };
        if (debeEncolar()) return queueAndFake(item, netErrorRes(), syntheticRows(rows), quien);
        const res = await raw.upsert(rows, conflictCol, { signal: timeoutSignal(attemptMs(rows, OFFLINE_ATTEMPT_MS)) });
        if (res && res.error && res.__network) return queueAndFake(item, res, syntheticRows(rows), quien);
        return res;
      },
      update: async (data, filter) => {
        const quien = capturaDe();
        const item = { table, op: 'update', payload: data, filter };
        if (debeEncolar()) return queueAndFake(item, netErrorRes(), syntheticRows([data]), quien);
        const res = await raw.update(data, filter, { signal: timeoutSignal(attemptMs(data, OFFLINE_ATTEMPT_MS)) });
        if (res && res.error && res.__network) return queueAndFake(item, res, syntheticRows([data]), quien);
        return res;
      },
      delete: async (filter) => {
        const quien = capturaDe();
        const item = { table, op: 'delete', filter };
        const synthetic = () => { const arr = []; arr.__offline = true; return arr; };
        if (debeEncolar()) return queueAndFake(item, netErrorRes(), synthetic(), quien);
        const res = await raw.delete(filter, { signal: timeoutSignal(OFFLINE_ATTEMPT_MS) });
        if (res && res.error && res.__network) return queueAndFake(item, res, synthetic(), quien);
        return res;
      },
    };
  };

  return {
    // setToken ya NO drena la cola (2026-09-16): activar un token no es aceptar a
    // la persona. Si el token es de otra persona, la cola se queda sin dueño.
    setToken: (t) => {
      _token = t || null;
      if (!_token) { fijarDueno(null); return; }
      const sub = subDelToken(_token);
      if (_duenoCola && sub && sub !== _duenoCola) fijarDueno(null);
    },
    getToken:           ()   => _token,
    setRefreshToken: (t) => {
      _refreshToken = t || null;
      if (!_refreshToken) { cancelarReintento(); _reintentoEspera = 0; }
    },
    getRefreshToken:    ()   => _refreshToken,
    // setUser recibe un perfil YA ACEPTADO (activo, no pendiente): lo marca como
    // dueño de la cola si coincide con el token, y la cola sube lo suyo.
    // { colaEnEspera: true } es la sesión restaurada al abrir la app (App.js): la cola
    // la cuenta como suya (el chip y la salida dicen lo suyo, las escrituras vivas
    // respetan el orden) pero no sube nada hasta que Auth y el perfil respondan y App
    // llame drenarCola. Así una cuenta bloqueada o desactivada no manda sus capturas a
    // dead-letter con un JWT que aún no vencía. Otra persona, o null, reinicia la espera;
    // la misma persona sin la opción (perfil refrescado, firma de correo) la conserva.
    setUser: (u, opciones) => {
      const enEspera = !!(opciones && opciones.colaEnEspera);
      const misma = !!(_user && u && _user.id && _user.id === u.id);
      _user = u || null;
      if (!misma || enEspera) _colaEnEspera = enEspera && !!_user;
      if (!_user || !_user.id) { fijarDueno(null); return; }
      const sub = subDelToken(_token);
      fijarDueno(!sub || sub === _user.id ? _user.id : null);
    },
    getUser:            ()   => _user,
    // El perfil está aceptado: la cola sube lo suyo ya. El login la llama tras aceptar
    // el perfil (después de setUser) y la sesión restaurada cuando Auth y el perfil
    // respondieron sin cerrarla. Devuelve lo que devuelve outbox.drain.
    drenarCola: () => {
      _colaEnEspera = false;
      return _duenoCola ? outbox.drain({ perfilId: _duenoCola }) : Promise.resolve('sin_dueno');
    },
    // 'ok' | 'rechazado' | 'sin_red'
    refreshSession:     doRefresh,
    // fn(motivo): 'sesion_cerrada' | 'por_seguridad' | 'acceso_desactivado'
    setOnSessionExpired:(fn) => { _onSessionExpired = fn; },

    // ── Edge Functions ────────────────────────────────────────────
    // Invoca /functions/v1/<name> con el JWT del usuario (reusa fetchWithRetry
    // para refrescar el token si expiró). Devuelve el JSON, o {error} si falla.
    invokeFunction: async (name, body) => {
      // 'Prefer' es una cabecera de PostgREST; en Edge Functions solo añade un header
      // al preflight CORS (y rompe el envío si la función no lo permite). Se omite.
      const headers = apiHeaders(_token);
      delete headers.Prefer;
      const opts = {
        method: "POST",
        headers,
        body: JSON.stringify(body || {}),
      };
      const r = await fetchWithRetry(`${SUPABASE_URL}/functions/v1/${name}`, opts);
      let data = null;
      try { data = await r.json(); } catch (e) { /* sin cuerpo JSON */ }
      if (!r.ok) return { error: (data && (data.error || data.message)) || `HTTP ${r.status}` };
      return data;
    },

    // ── Auth ──────────────────────────────────────────────────────
    // Todas devuelven ResultadoAuth { ok, status, datos, cuerpo, errorRed } (ver
    // acceso/acceso.logic.js), nunca lanzan y tienen tiempo límite. El fallo se
    // clasifica con clasificarFalloAuth y se dice con textoAcceso. Ninguna activa
    // un token ni toca la cola: eso lo decide quien llama, tras aceptar el perfil.
    auth: {
      // POST /token?grant_type=password. datos = { access_token, refresh_token,
      // expires_in, expires_at, token_type, user }. Compatibilidad: en éxito esos
      // campos también van al nivel superior; en fallo, error { message, code }.
      signIn: async (email, password) =>
        conCompat(await llamarAuth("token?grant_type=password", { cuerpo: { email, password } }), "entrar", { repetirDatos: true }),

      // POST /recover?redirect_to=<sitio>/restablecer. Auth solo lee redirect_to de la
      // query (en el cuerpo lo ignoraba y el enlace caía al Site URL). El destino
      // sale SIEMPRE de sitio.js: se ignora cualquier otro argumento.
      // Éxito: 200 {} exista o no la cuenta.
      resetPassword: async (email) =>
        conCompat(await llamarAuth(`recover?redirect_to=${encodeURIComponent(urlRestablecer())}`, { cuerpo: { email } }), "recuperar"),

      // POST /verify con { type, token_hash } y nada más (Auth rechaza email o
      // redirect_to junto a token_hash). Gasta el token: se llama al pulsar
      // Guardar, no al abrir el enlace. datos = sesión de recuperación
      // { access_token, refresh_token, ..., user: { email } }.
      verificarOtp: async ({ type, token_hash } = {}) =>
        conCompat(await llamarAuth("verify", { cuerpo: { type, token_hash } }), "nueva"),

      // PUT /user { password[, current_password] } con el token EXPLÍCITO (el del
      // enlace o el de la sesión; nunca se toma el vigente por su cuenta). datos =
      // el usuario (incluye email). Auth cierra las DEMÁS sesiones de la persona y
      // conserva la del token usado. Con weak_password o same_password se puede
      // reintentar con el mismo token.
      updatePassword: async (password, token, { currentPassword } = {}) => {
        const modo = currentPassword ? "cambiar" : "nueva";
        if (!token) {
          return conCompat({ ok: false, status: 401, datos: null, cuerpo: { error_code: "no_authorization", msg: "sin token" }, errorRed: null }, modo);
        }
        const cuerpo = { password };
        if (currentPassword) cuerpo.current_password = currentPassword;
        return conCompat(await llamarAuth("user", { metodo: "PUT", token, cuerpo }), modo, { repetirDatos: true });
      },

      // Confirma la contraseña de alguien sin tocar la sesión vigente: password
      // grant y cierre (scope=local) de ESA sesión nueva, sin esperarlo. datos =
      // { user } sin tokens. Para acciones delicadas como Republicar.
      verificarContrasena: async (email, password) => {
        const res = await llamarAuth("token?grant_type=password", { cuerpo: { email, password } });
        if (!res.ok) return conCompat(res, "confirmar");
        const nuevo = res.datos && res.datos.access_token;
        if (nuevo) llamarAuth("logout?scope=local", { token: nuevo, tiempoMs: AUTH_SALIDA_MS });
        return { ...res, datos: { user: (res.datos && res.datos.user) || null } };
      },

      // El servidor OAuth 2.1 de Supabase (2026-10-28, la vista de permiso del
      // conector de claude.ai, acceso/permiso.logic.js). Con el token EXPLÍCITO de la
      // sesión que decide. GET /oauth/authorizations/:id: datos = { authorization_id,
      // redirect_uri, client: { id, name, uri, logo_uri }, user: { id, email }, scope }
      // o, si el permiso ya se había dado, { redirect_url }. POST .../consent con
      // { action: 'approve' | 'deny' }: datos = { redirect_url } (de vuelta a quien pidió).
      solicitudOAuth: async (authorizationId, token) =>
        llamarAuth(`oauth/authorizations/${encodeURIComponent(authorizationId)}`, { metodo: "GET", token }),
      decidirOAuth: async (authorizationId, accion, token) =>
        llamarAuth(`oauth/authorizations/${encodeURIComponent(authorizationId)}/consent`, {
          token, cuerpo: { action: accion === "approve" ? "approve" : "deny" },
        }),

      // POST /logout?scope=local (por defecto) con tiempo límite de 4 s. Sin scope
      // Auth cierra TODAS las sesiones de la persona (el otro equipo, la tablet).
      // Si el token es el de la sesión vigente, lo local se limpia PRIMERO (memoria
      // y almacén) y la red va después: la pantalla no espera al servidor.
      // keepalive: la petición sale aunque la página se esté cerrando (pagehide).
      signOut: async (token, { scope = "local", keepalive = false } = {}) => {
        const alcance = scope === "global" || scope === "others" ? scope : "local";
        if (token && token === _token) {
          _token = null;
          _refreshToken = null;
          _user = null;
          cancelarReintento();
          _reintentoEspera = 0;
          fijarDueno(null);
          // El almacén se borra si guarda ESTA sesión: otra ventana de la app
          // instalada puede tener guardada la de otra persona.
          const guardado = getAuthItem("sb_token");
          if (!guardado || guardado === token) clearAuth();
        }
        if (!token) return { ok: true, status: 0, datos: null, cuerpo: null, errorRed: null };
        return conCompat(await llamarAuth(`logout?scope=${alcance}`, { token, tiempoMs: AUTH_SALIDA_MS, keepalive: !!keepalive }), "entrar");
      },
    },

    // ── Database (REST) ───────────────────────────────────────────
    // Tablas de captura de campo pasan por la capa offline (cola en red
    // caída); el resto conserva el comportamiento histórico exacto.
    from: (table) => OFFLINE_TABLES.has(table) ? offlineFrom(table) : rawFrom(table),

    // Cliente crudo SIN cola, para flujos destructivos multi-paso que deben
    // ser solo-online (ej. eliminar obra en cascada): offline fallan rapido
    // y completo, jamas dejan mitades encoladas.
    fromDirect: (table) => rawFrom(table),

    // ── RPC (funciones SECURITY DEFINER) ──────────────────────────
    rpc: async (fn, params = {}) => {
      if (!OFFLINE_RPCS.has(fn)) return rawRpc(fn, params);
      const quien = capturaDe();
      // RPC de captura encolable: inyectar id de muestra de cliente SIEMPRE
      // (online también) para que un replay tras corte a media respuesta
      // sea detectable por PK y no duplique muestra + especímenes.
      const p = { ...params };
      if ((fn === 'crear_muestra_con_especimenes' || fn === 'lab_registrar_muestra')
          && p.p_muestra && !p.p_muestra.id) {
        p.p_muestra = { ...p.p_muestra, id: genId() };
      }
      // lab_registrar_muestra también inserta hijos: sus ids viajan del
      // cliente por la misma razón (el replay choca en PK, 23505 = aplicado).
      if (fn === 'lab_registrar_muestra' && Array.isArray(p.p_ensayes)) {
        p.p_ensayes = p.p_ensayes.map(e => (e && !e.id) ? { ...e, id: genId() } : e);
      }
      const item = { table: `rpc:${fn}`, op: 'rpc', rpcName: fn, payload: p };
      // Los callers solo checan error; el shape simula la fila devuelta.
      const synthetic = () => ({ ...(p.p_muestra || {}), __offline: true });
      if (debeEncolar()) return queueAndFake(item, netErrorRes(), synthetic(), quien);
      const res = await rawRpc(fn, p, { signal: timeoutSignal(OFFLINE_ATTEMPT_MS) });
      if (res && res.error && res.__network) return queueAndFake(item, res, synthetic(), quien);
      return res;
    },

    // ── Replay del outbox (lo invoca offlineQueue, no la app) ─────
    // ok=true            → aplicado, sale de la cola
    // retriable=true     → red caída; el drenado se detiene y reintenta después
    // ajeno=true         → la sesión es de otra persona; el item sigue pendiente
    // error='...'        → rechazo real del servidor; el item pasa a dead-letter
    _replayOutboxItem: async (item) => {
      // Sin sesión todavía (arranque, restore en curso) o con la sesión restaurada
      // esperando a Auth y al perfil: esperar, no fallar.
      if (!_token || _colaEnEspera) return { ok: false, retriable: true };
      // La sesión vigente no es de quien capturó: no se sube con otra identidad.
      const quien = subDelToken(_token);
      if (item.perfilId && quien && quien !== item.perfilId) return { ok: false, ajeno: true };
      const opts = { signal: timeoutSignal(attemptMs(item.payload, OFFLINE_REPLAY_MS)) };
      let res;
      if (item.op === 'insert')      res = await rawFrom(item.table).insert(item.payload, opts);
      else if (item.op === 'update') res = await rawFrom(item.table).update(item.payload, item.filter, opts);
      else if (item.op === 'upsert') res = await rawFrom(item.table).upsert(item.payload, item.conflictCol || 'id', opts);
      else if (item.op === 'delete') res = await rawFrom(item.table).delete(item.filter, opts);
      else if (item.op === 'rpc')    res = await rawRpc(item.rpcName, item.payload, opts);
      else return { ok: false, error: `op desconocida: ${item.op}` };
      if (res && res.error) {
        if (res.__network) return { ok: false, retriable: true };
        // La sesión se cerró mientras subía (renovación rechazada): esperar al
        // siguiente inicio de sesión de su dueño, nunca dead-letter.
        if (!_token) return { ok: false, retriable: true };
        // Salió y entró otra persona mientras subía: fetchWithRetry no reenvía con su
        // identidad y el rechazo no es de este registro. Espera a su dueño.
        if (subDelToken(_token) !== quien) return item.perfilId ? { ok: false, ajeno: true } : { ok: false, retriable: true };
        // Sesion vencida (refresh rechazado) o conflicto transitorio de la
        // BD: se reparan solos (re-login -> perfil aceptado -> drain; o el retry).
        // Jamas dead-letter por esto — mataria la captura y a sus hijos por FK.
        const code = res.error.code;
        if (code === 'PGRST301' || code === 'PGRST303' || code === '40001' || code === '40P01'
            || /jwt|sesi.n vencida/i.test(res.error.message || '')) {
          return { ok: false, retriable: true };
        }
        // Insert/RPC con UUID de cliente: PK duplicado = el request original
        // SÍ llegó al servidor antes del corte. Ya aplicado.
        if ((item.op === 'insert' || item.op === 'rpc') && code === '23505') return { ok: true };
        return { ok: false, error: [code, res.error.message].filter(Boolean).join(' ') };
      }
      // Update que no matcheo filas (registro borrado o guard de concurrencia
      // tipo revision=eq): no fingir exito silencioso — dead-letter visible.
      if (item.op === 'update' && Array.isArray(res) && res.length === 0) {
        return { ok: false, error: 'update no afecto filas (registro inexistente o guard de concurrencia)' };
      }
      return { ok: true };
    },

    // ── Storage ───────────────────────────────────────────────────
    storage: {
      upload: async (bucket, path, file) => {
        try {
          // Intentar POST (archivo nuevo), si existe usar PUT (reemplazar)
          const url = `${SUPABASE_URL}/storage/v1/object/${bucket}/${path}`;
          const headers = {
            "apikey": SUPABASE_KEY,
            "Authorization": `Bearer ${_token}`,
            "Content-Type": file.type || "application/octet-stream",
            "x-upsert": "true",  // permite sobreescribir si ya existe
          };
          const r = await fetch(url, { method: "POST", headers, body: file });
          const d = await r.json();
          if (!r.ok) {
            devLog("Storage upload error:", d);
            return { error: sanitizeError({ message: d.message || d.error || JSON.stringify(d), code: d.code || '' }) };
          }
          return d;
        } catch(e) { return { error: sanitizeError(e.message) }; }
      },
      getUrl: (bucket, path) =>
        `${SUPABASE_URL}/storage/v1/object/authenticated/${bucket}/${path}`,
      remove: async (bucket, paths) => {
        try {
          const r = await fetch(`${SUPABASE_URL}/storage/v1/object/${bucket}`, {
            method: "DELETE",
            headers: { "apikey": SUPABASE_KEY, "Authorization": `Bearer ${_token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ prefixes: paths }),
          });
          return r.json();
        } catch(e) { return { error: sanitizeError(e.message) }; }
      },
    },
  };
})();


// ── Arranque de la cola offline ───────────────────────────────────
// El ejecutor replays con el token vigente (fetchWithRetry refresca si
// expiró durante la jornada offline). El drenado se dispara al volver la
// red (listener en outbox.init), al aceptar o restaurar un perfil (setUser
// fija el dueño; sb.drenarCola lo pide explícito) y con un reintento suave
// mientras haya pendientes. Sin dueño no sube nada.
if (typeof window !== 'undefined' && 'indexedDB' in window) {
  outbox.setExecutor(sb._replayOutboxItem);
  outbox.init();
}

// ══════════════════════════════════════════════
//  UTILIDADES
// ══════════════════════════════════════════════

// Re-exportar sanitizeError por si algún caller necesita sanitizar manualmente
// un error que no vino del cliente (ej. Promise.reject ad-hoc).
export { sb, sanitizeError };
